import { useState, useEffect } from "react";
import { 
  Clock, CheckCircle, XCircle, Monitor, Smartphone, Tablet,
  Globe, RefreshCw, Filter, ChevronLeft, ChevronRight, Search,
  Trash2, BarChart3, Users, Calendar
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Composant principal
export default function ConnectionHistory() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0, has_more: false });
  
  // Filtres
  const [emailFilter, setEmailFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState("logs"); // logs ou stats

  const loadHistory = async (skip = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("skip", skip);
      params.append("limit", pagination.limit);
      if (emailFilter) params.append("email", emailFilter);
      if (statusFilter) params.append("success", statusFilter);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      
      const resp = await authFetch(`${API}/api/admin/connection-history?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setLogs(data.logs || []);
        setPagination({
          ...pagination,
          skip: data.pagination?.skip || 0,
          total: data.total || 0,
          has_more: data.pagination?.has_more || false
        });
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const resp = await authFetch(`${API}/api/admin/connection-stats`);
      if (resp.ok) {
        setStats(await resp.json());
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadHistory(0);
    loadStats();
  }, []);

  const handleFilter = () => {
    loadHistory(0);
  };

  const handleClearFilters = () => {
    setEmailFilter("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setTimeout(() => loadHistory(0), 100);
  };

  const handlePrevPage = () => {
    const newSkip = Math.max(0, pagination.skip - pagination.limit);
    loadHistory(newSkip);
  };

  const handleNextPage = () => {
    loadHistory(pagination.skip + pagination.limit);
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    const d = new Date(isoDate);
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case "mobile": return <Smartphone className="w-4 h-4" />;
      case "tablet": return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header avec onglets */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("logs")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "logs" ? "bg-blue-600 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Historique
          </button>
          <button
            onClick={() => setActiveTab("stats")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "stats" ? "bg-blue-600 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Statistiques
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-lg text-sm ${showFilters ? "bg-amber-600" : "bg-slate-700"} text-white`}
          >
            <Filter className="w-4 h-4 inline mr-1" />
            Filtres
          </button>
          <button
            onClick={() => { loadHistory(pagination.skip); loadStats(); }}
            className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Email</label>
              <input
                type="text"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                placeholder="Rechercher..."
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Statut</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">Tous</option>
                <option value="true">Réussies</option>
                <option value="false">Échouées</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Date début</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Date fin</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleFilter}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-sm"
            >
              <Search className="w-4 h-4 inline mr-1" />
              Rechercher
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white text-sm"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      )}

      {/* Contenu - Onglet Logs */}
      {activeTab === "logs" && (
        <>
          {/* Stats rapides */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <p className="text-2xl font-bold text-white">{pagination.total}</p>
              <p className="text-sm text-slate-400">Total connexions</p>
            </div>
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
              <p className="text-2xl font-bold text-green-400">{stats?.total?.success || 0}</p>
              <p className="text-sm text-green-300">Réussies</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <p className="text-2xl font-bold text-red-400">{stats?.total?.failure || 0}</p>
              <p className="text-sm text-red-300">Échouées</p>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-2xl font-bold text-blue-400">{stats?.total?.success_rate || 0}%</p>
              <p className="text-sm text-blue-300">Taux de succès</p>
            </div>
          </div>

          {/* Liste des connexions */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Statut</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Device</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Navigateur</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">OS</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin mx-auto" />
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-slate-400">
                        Aucune connexion enregistrée
                      </td>
                    </tr>
                  ) : (
                    logs.map((log, i) => (
                      <tr key={i} className="hover:bg-slate-700/30">
                        <td className="px-4 py-3 text-sm text-slate-300 whitespace-nowrap">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-sm text-white font-medium">
                          {log.email}
                        </td>
                        <td className="px-4 py-3">
                          {log.success ? (
                            <span className="inline-flex items-center gap-1 text-green-400 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              Réussi
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-400 text-sm" title={log.failure_reason}>
                              <XCircle className="w-4 h-4" />
                              Échoué
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          <span className="inline-flex items-center gap-1">
                            {getDeviceIcon(log.device_type)}
                            {log.device_type || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {log.browser || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {log.os || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400 font-mono">
                          {log.ip_address || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 bg-slate-900/50 flex items-center justify-between">
              <p className="text-sm text-slate-400">
                Affichage {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} sur {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={pagination.skip === 0}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!pagination.has_more}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Contenu - Onglet Statistiques */}
      {activeTab === "stats" && stats && (
        <div className="space-y-6">
          {/* Stats 7 derniers jours */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-400" />
              7 derniers jours
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{stats.last_7_days?.total || 0}</p>
                <p className="text-sm text-slate-400">Connexions</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-400">{stats.last_7_days?.success || 0}</p>
                <p className="text-sm text-slate-400">Réussies</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-red-400">{stats.last_7_days?.failure || 0}</p>
                <p className="text-sm text-slate-400">Échouées</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-400">{stats.last_7_days?.success_rate || 0}%</p>
                <p className="text-sm text-slate-400">Taux de succès</p>
              </div>
            </div>
            
            {/* Graphique journalier simple */}
            {stats.daily && stats.daily.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-slate-400 mb-3">Connexions par jour</h4>
                <div className="flex items-end justify-between gap-2 h-32">
                  {stats.daily.map((day, i) => {
                    const maxVal = Math.max(...stats.daily.map(d => d.success + d.failure));
                    const totalHeight = maxVal > 0 ? ((day.success + day.failure) / maxVal) * 100 : 0;
                    const successHeight = maxVal > 0 ? (day.success / maxVal) * 100 : 0;
                    
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div className="w-full relative" style={{ height: `${totalHeight}%`, minHeight: "4px" }}>
                          <div 
                            className="absolute bottom-0 w-full bg-green-500 rounded-t"
                            style={{ height: `${totalHeight > 0 ? (day.success / (day.success + day.failure)) * 100 : 0}%` }}
                          />
                          <div 
                            className="absolute top-0 w-full bg-red-500 rounded-t"
                            style={{ height: `${totalHeight > 0 ? (day.failure / (day.success + day.failure)) * 100 : 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-2">{day.date.split("-")[2]}/{day.date.split("-")[1]}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-center gap-4 mt-2">
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-500 rounded"></span> Réussies
                  </span>
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <span className="w-3 h-3 bg-red-500 rounded"></span> Échouées
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Top utilisateurs & Stats par device/navigateur */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Top utilisateurs */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-400" />
                Top utilisateurs
              </h3>
              <div className="space-y-2">
                {(stats.top_users || []).slice(0, 8).map((user, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span className="text-sm text-slate-300 truncate flex-1">{user.email}</span>
                    <span className="text-sm font-medium text-white ml-2">{user.count}</span>
                  </div>
                ))}
                {(!stats.top_users || stats.top_users.length === 0) && (
                  <p className="text-sm text-slate-400">Aucune donnée</p>
                )}
              </div>
            </div>

            {/* Par device */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Monitor className="w-5 h-5 text-green-400" />
                Par appareil
              </h3>
              <div className="space-y-2">
                {(stats.by_device || []).map((item, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span className="text-sm text-slate-300 flex items-center gap-2">
                      {getDeviceIcon(item.device)}
                      {item.device || "Inconnu"}
                    </span>
                    <span className="text-sm font-medium text-white">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Par navigateur */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-400" />
                Par navigateur
              </h3>
              <div className="space-y-2">
                {(stats.by_browser || []).map((item, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span className="text-sm text-slate-300">{item.browser || "Inconnu"}</span>
                    <span className="text-sm font-medium text-white">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
